'use client';

import {
  type Filter,
  PageSize,
  type PaginatedProduct,
  type Swatches,
} from '@dealerportal/common';
import { logger } from '@dealerportal/core';
import {
  cicMerchandisingTags,
  cicProductTypeName,
  cicProductTypeSlug,
  decodeItemCode,
  isSpreadItem,
  SlugEquals,
  sortWithPriority,
  toCiCBaseItemType,
  toKebab,
  toTitleCase,
} from '@dealerportal/utilities';
import isEmpty from 'es-toolkit/compat/isEmpty';
import { useCallback, useRef, useState } from 'react';
import slugify from 'slugify';

// Cache for item groups to avoid redundant API calls
const itemGroupsCache = new Map<string, Promise<IItemGroup[]>>();

// Constants
export const DEFAULT_PAGE_SIZE = PageSize;
const VARIANTS_FETCH_PAGE_SIZE = 100;
const MIN_FILTER_RESULT_SIZE = 0;

// Characteristic keys
const CHAR_TYPE = 'type';
const CHAR_CATALOGS = 'catalogs';
const CHAR_UPIDS = 'upids';
const CHAR_BRAND = 'brand';
const CHAR_LINE = 'line';
const CHAR_BRANDS = 'brands';
const CHAR_COLLECTIONS = 'collections';
const CHAR_COLLECTION = 'collection';
const CHAR_PRODUCT_LINES = 'productLines';
const GROUP_FACET_CODES: Record<string, true> = {
  brands: true,
  collections: true,
  productLines: true,
};
const CHAR_MERCHANDISINGS = 'merchandisings';
const CHAR_TAGS = 'tags';
const CHAR_SKUS = 'skus';

// Facet/Filter codes
const FACET_BASE_ITEM_TYPES = 'baseItemTypes';
const FILTER_VISUALIZABLE = 'visualizable';
const FILTER_SAMPLES = 'samples';
const FILTER_FAVORITES = 'Favorites';

// Item group types
const ITEM_GROUP_COLLECTION = 'itemGroup.collection';
const ITEM_GROUP_BRAND = 'itemGroup.brand';
const ITEM_GROUP_PRODUCT_LINE = 'itemGroup.productLine';

// Tag names
export const TAG_VISUALIZABLE = 'Visualizable';
export const TAG_SAMPLES = 'Samples';

// Special group ref
export const GROUP_REF_SRP_PRIORITY = 'srp_priority';

// ============================================================
// Types / Interfaces
// ============================================================

interface SearchItemsParams {
  catalogVersionId: string;
  characteristics: ICharacteristicsFilter;
  groupRefs?: string[];
  offset?: number;
  searchText: string;
  pageSize?: number;
}

interface CatalogConfig {
  excludedFilters?: string[];
  filterPriorities: Record<string, number>;
  filterConfig: Record<
    string,
    {
      displayName: string;
      uom?: string;
      restrictedTypes: string[];
      /**
       * `specs` translation keys overriding vendor value labels, keyed by facet
       * value code. Categorical facets only: ignored, with a warning, when
       * `uom` is also set.
       */
      valueDisplayNames?: Record<string, string>;
    }
  >;
}

export interface SearchResult {
  products: Partial<PaginatedProduct>[];
  filters: Filter[];
  colorCount: number;
  productCount: number;
}

export interface UseCiCSearchResult {
  search: (params: SearchParams) => Promise<SearchResult>;
  getFilters: (params: GetFiltersParams) => Promise<{
    filters: Filter[];
    total: number;
  }>;
  getSwatchesForProduct: (
    product: Partial<PaginatedProduct>
  ) => Promise<Swatches[]>;
  isLoading: boolean;
  error: string | null;
}

interface GetFiltersParams {
  catalogVersionId: string;
  characteristics: ICharacteristicsFilter;
  groupRefs?: string[];
  searchText?: string;
}

export interface SearchParams extends GetFiltersParams {
  initialPage: number;
  excludeFilters: boolean;
  excludeVariants?: boolean;
  pageSize?: number;
  skus?: string[];
}

export enum MerchandisingType {
  WebInStock = 'webinstock',
  WebBestSeller = 'webbestseller',
  WebFeatured = 'webfeatured',
  WebOnClearance = 'webonclearance',
  WebOnSale = 'webonsale',
  WebSpecialOrder = 'webspecialorder',
  WebVisualizable = 'webvisualizable',
  WebSampled = 'websampled',
}

export enum CatalogCategory {
  Flooring = 'flooring',
  Cabinetry = 'cabinetry',
  Bath = 'bath',
  Worktop = 'worktop',
}

// ============================================================
// Item Category Strategy
// ============================================================

export interface ItemCategoryStrategy {
  readonly category: CatalogCategory;
  readonly typePrefix: string;
  getPriceDescription(baseItemType: string): string;
  mapTypeForAPI(baseItemType: string): string;
  mapTypeForUrl(value: string): string;
  cleanTypePrefix(baseItemType: string): string;
  /** Returns the strategy responsible for a fully-qualified baseItemType from the API. */
  resolveForBaseItemType(baseItemType: string): ItemCategoryStrategy;
}

export class FlooringStrategy implements ItemCategoryStrategy {
  readonly category = CatalogCategory.Flooring;
  readonly typePrefix = 'covering.';

  getPriceDescription(_baseItemType: string): string {
    return 'Sq Ft';
  }

  mapTypeForAPI(baseItemType: string): string {
    return toCiCBaseItemType(baseItemType);
  }

  mapTypeForUrl(value: string): string {
    return cicProductTypeSlug(toCiCBaseItemType(value));
  }

  cleanTypePrefix(baseItemType: string): string {
    return baseItemType.replace(this.typePrefix, '');
  }

  resolveForBaseItemType(_baseItemType: string): ItemCategoryStrategy {
    return this;
  }
}

export class CabinetryStrategy implements ItemCategoryStrategy {
  readonly category = CatalogCategory.Cabinetry;
  readonly typePrefix = 'cabinetry.';

  getPriceDescription(_baseItemType: string): string {
    return 'Per Unit';
  }

  mapTypeForAPI(baseItemType: string): string {
    return baseItemType.startsWith(this.typePrefix)
      ? baseItemType
      : `${this.typePrefix}${baseItemType}`;
  }

  mapTypeForUrl(value: string): string {
    return value;
  }

  cleanTypePrefix(baseItemType: string): string {
    // return baseItemType.replace(this.typePrefix, '');
    return baseItemType;
  }

  resolveForBaseItemType(_baseItemType: string): ItemCategoryStrategy {
    return this;
  }
}

export class BathStrategy implements ItemCategoryStrategy {
  readonly category = CatalogCategory.Bath;
  readonly typePrefix = 'bathing.';

  getPriceDescription(_baseItemType: string): string {
    return 'Per Unit';
  }

  mapTypeForAPI(baseItemType: string): string {
    return baseItemType.startsWith(this.typePrefix)
      ? baseItemType
      : `${this.typePrefix}${baseItemType}`;
  }

  mapTypeForUrl(value: string): string {
    return value;
  }

  cleanTypePrefix(baseItemType: string): string {
    return baseItemType.replace(this.typePrefix, '');
  }

  resolveForBaseItemType(_baseItemType: string): ItemCategoryStrategy {
    return this;
  }
}

// ============================================================
// Composite Strategy — multi-category searches
// ============================================================

export class CompositeStrategy implements ItemCategoryStrategy {
  readonly category: CatalogCategory;
  readonly typePrefix = '';

  constructor(private readonly strategies: ItemCategoryStrategy[]) {
    if (strategies.length === 0)
      throw new Error('CompositeStrategy requires at least one strategy');
    this.category = strategies[0].category;
  }

  private findByPrefix(baseItemType: string): ItemCategoryStrategy | undefined {
    return this.strategies.find(
      (s) => s.typePrefix && baseItemType.startsWith(s.typePrefix)
    );
  }

  getPriceDescription(baseItemType: string): string {
    return (
      this.findByPrefix(baseItemType) ?? this.strategies[0]
    ).getPriceDescription(baseItemType);
  }

  mapTypeForAPI(baseItemType: string): string {
    return (
      this.findByPrefix(baseItemType) ?? this.strategies[0]
    ).mapTypeForAPI(baseItemType);
  }

  mapTypeForUrl(value: string): string {
    // loop through strategies to find the one that can handle this value
    for (const strategy of this.strategies) {
      const remapped = strategy.mapTypeForUrl(value);
      if (remapped !== value) {
        return remapped;
      }
    }
    return value;
  }

  cleanTypePrefix(baseItemType: string): string {
    const strategy = this.resolveForBaseItemType(baseItemType);
    return strategy ? strategy.cleanTypePrefix(baseItemType) : baseItemType;
  }

  resolveForBaseItemType(baseItemType: string): ItemCategoryStrategy {
    return this.findByPrefix(baseItemType) ?? this.strategies[0];
  }
}

export class WorktopStrategy implements ItemCategoryStrategy {
  readonly category = CatalogCategory.Worktop;
  readonly typePrefix = 'worktop.';

  getPriceDescription(_baseItemType: string): string {
    return 'Per Unit';
  }

  mapTypeForAPI(baseItemType: string): string {
    return baseItemType.startsWith(this.typePrefix)
      ? baseItemType
      : `${this.typePrefix}${baseItemType}`;
  }

  mapTypeForUrl(value: string): string {
    return value;
  }

  cleanTypePrefix(baseItemType: string): string {
    return baseItemType.replace(this.typePrefix, '');
  }

  resolveForBaseItemType(_baseItemType: string): ItemCategoryStrategy {
    return this;
  }
}

const CATEGORY_STRATEGIES: Record<CatalogCategory, ItemCategoryStrategy> = {
  [CatalogCategory.Flooring]: new FlooringStrategy(),
  [CatalogCategory.Cabinetry]: new CabinetryStrategy(),
  [CatalogCategory.Bath]: new BathStrategy(),
  [CatalogCategory.Worktop]: new WorktopStrategy(),
};

export const getItemCategoryStrategy = (
  category: CatalogCategory
): ItemCategoryStrategy => CATEGORY_STRATEGIES[category];

export const createStrategy = (
  categories: CatalogCategory | CatalogCategory[]
): ItemCategoryStrategy => {
  const arr = Array.isArray(categories) ? categories : [categories];
  if (arr.length === 1) return getItemCategoryStrategy(arr[0]);
  return new CompositeStrategy(arr.map(getItemCategoryStrategy));
};

// ============================================================
// Merchandising helpers
// ============================================================

const getDisplayNameForTag = (key: string): string => {
  const displayNameMap: Record<string, string> = {
    [MerchandisingType.WebInStock]: 'In Stock',
    [MerchandisingType.WebBestSeller]: 'Best Seller',
    [MerchandisingType.WebFeatured]: 'Featured',
    [MerchandisingType.WebOnClearance]: 'On Clearance',
    [MerchandisingType.WebOnSale]: 'On Sale',
    [MerchandisingType.WebSpecialOrder]: 'Special Order',
    [MerchandisingType.WebVisualizable]: 'Visualizable',
    [MerchandisingType.WebSampled]: 'Samples',
  };
  return displayNameMap[key.toLowerCase()] || toTitleCase(key);
};

// ============================================================
// Shared utilities (not category-specific)
// ============================================================

const sanitizeCharacteristics = (
  characteristics: ICharacteristicsFilter
): ICharacteristicsFilter => {
  const sanitized = { ...characteristics };
  delete sanitized[CHAR_TYPE];
  delete sanitized[CHAR_CATALOGS];
  delete sanitized[CHAR_UPIDS];
  delete sanitized[CHAR_BRAND];
  delete sanitized[CHAR_LINE];
  delete sanitized[CHAR_COLLECTION];
  delete sanitized[CHAR_SKUS];
  return sanitized;
};

const fetchCatalogConfig = async (): Promise<CatalogConfig> => {
  try {
    const baseUrl =
      process.env.NEXT_PUBLIC_CATALOG_CONFIG_URL ??
      'http://localhost:4200/config/catalog.json';
    const buildId = process.env.NEXT_PUBLIC_BUILD_ID;
    const version = buildId || Date.now().toString();
    const response = await fetch(`${baseUrl}?v=${version}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    logger.withError(error).error('Error fetching catalog config:');
    throw new Error('Failed to fetch catalog config');
  }
};

const deduplicateFacets = (facets: IFacet[]): IFacet[] =>
  facets.reduce((acc: IFacet[], facet: IFacet) => {
    if (!acc.some((f) => f.code.toLowerCase() === facet.code.toLowerCase())) {
      acc.push(facet);
    }
    return acc;
  }, []);

/**
 * Depth-first list of a group tree: every group plus all of its descendants.
 * Three-level catalogs nest brand > collection > product line, and every
 * consumer here matches groups by `id` or `type` on a flat list.
 */
const flattenGroups = (groups: IItemGroup[]): IItemGroup[] => {
  const flat: IItemGroup[] = [];
  const visit = (group: IItemGroup) => {
    flat.push(group);
    for (const child of group.groups ?? []) visit(child);
  };
  for (const group of groups) visit(group);
  return flat;
};

const fetchItemGroups = (catalogVersionId: string): Promise<IItemGroup[]> => {
  if (!itemGroupsCache.has(catalogVersionId)) {
    itemGroupsCache.set(
      catalogVersionId,
      globalThis.CiCAPI.content.v2
        .getCatalogVersionItemGroups(catalogVersionId)
        .then((response) => {
          const roots = response.result ?? [];
          return flattenGroups(
            roots.flatMap((itemGroup) => itemGroup.groups ?? [])
          );
        })
        .catch((error) => {
          itemGroupsCache.delete(catalogVersionId);
          logger.withError(error).error('Error fetching item groups:');
          return [];
        })
    );
  }
  return itemGroupsCache.get(catalogVersionId)!;
};

interface GroupLevel {
  /** DealerPortal filter code and URL/characteristics key (`brand`, `collection`, `line`). */
  filterCode: string;
  /** CiC facet code and `classificationFilter.characteristics` key. */
  facetCode: string;
  /** `filterConfig` key holding the label for this level. */
  configKey: string;
  /** CiC item group `type`. */
  groupType: string;
}

// `@broadlume/client-api-types` predates `itemGroup.productLine`; the API emits it.
// Keyed on the array returned by fetchItemGroups, which is cached per catalog version.
const productLineGroupsCache = new WeakMap<IItemGroup[], boolean>();

const hasProductLineGroups = (itemGroups: IItemGroup[]): boolean => {
  const cached = productLineGroupsCache.get(itemGroups);
  if (cached !== undefined) return cached;
  const found = itemGroups.some(
    (ig) => (ig.type as string | undefined) === ITEM_GROUP_PRODUCT_LINE
  );
  productLineGroupsCache.set(itemGroups, found);
  return found;
};

/**
 * Item group type that represents a product line: catalogs with three group
 * levels use `itemGroup.productLine`, older two-level catalogs use
 * `itemGroup.collection`.
 */
const productLineGroupType = (itemGroups: IItemGroup[]): string =>
  hasProductLineGroups(itemGroups)
    ? ITEM_GROUP_PRODUCT_LINE
    : ITEM_GROUP_COLLECTION;

/** Ordered brand -> (collection) -> line hierarchy for the current catalog. */
const resolveGroupLevels = (itemGroups: IItemGroup[]): GroupLevel[] => {
  const brand = {
    filterCode: CHAR_BRAND,
    facetCode: CHAR_BRANDS,
    configKey: CHAR_BRANDS,
    groupType: ITEM_GROUP_BRAND,
  };
  if (!hasProductLineGroups(itemGroups)) {
    return [
      brand,
      {
        filterCode: CHAR_LINE,
        facetCode: CHAR_COLLECTIONS,
        configKey: CHAR_PRODUCT_LINES,
        groupType: ITEM_GROUP_COLLECTION,
      },
    ];
  }
  return [
    brand,
    {
      filterCode: CHAR_COLLECTION,
      facetCode: CHAR_COLLECTIONS,
      configKey: CHAR_COLLECTIONS,
      groupType: ITEM_GROUP_COLLECTION,
    },
    {
      filterCode: CHAR_LINE,
      facetCode: CHAR_PRODUCT_LINES,
      configKey: CHAR_PRODUCT_LINES,
      groupType: ITEM_GROUP_PRODUCT_LINE,
    },
  ];
};

const mapMerchandisingsToTags = (
  filters: Filter[],
  initialCharacteristics: ICharacteristicsFilter
): void => {
  let hasVisualization = false;
  let hasSamples = false;
  const initialMerchandisings =
    initialCharacteristics[CHAR_MERCHANDISINGS] || [];
  const filter = filters.find((f) => f.code === CHAR_MERCHANDISINGS);

  if (filter) {
    filter.code = CHAR_TAGS;
    filter.name = 'Tags';
    filter.urlFriendlyName = toKebab(CHAR_TAGS);

    if (filter.potentialValues[MerchandisingType.WebVisualizable]) {
      delete filter.potentialValues[MerchandisingType.WebVisualizable];
      hasVisualization = true;
    }
    if (filter.potentialValues[MerchandisingType.WebSampled]) {
      delete filter.potentialValues[MerchandisingType.WebSampled];
      hasSamples = true;
    }

    const updatedPotentialValues: Record<string, number> = {};
    for (const [key, value] of Object.entries(filter.potentialValues)) {
      updatedPotentialValues[getDisplayNameForTag(key)] = value;
    }

    filter.enabledValues = initialMerchandisings
      .map((tag) => getDisplayNameForTag(tag))
      .filter((val) => val !== TAG_SAMPLES && val !== TAG_VISUALIZABLE);
    filter.potentialValues = updatedPotentialValues;
  }

  filters.push({
    name: TAG_VISUALIZABLE,
    code: FILTER_VISUALIZABLE,
    urlFriendlyName: toKebab(FILTER_VISUALIZABLE),
    potentialValues: hasVisualization ? { true: 1 } : { true: 0 },
    enabledValues: initialMerchandisings.includes(
      MerchandisingType.WebVisualizable
    )
      ? ['1']
      : [],
    displayNames: { true: TAG_VISUALIZABLE },
  });

  filters.push({
    name: TAG_SAMPLES,
    code: FILTER_SAMPLES,
    urlFriendlyName: toKebab(FILTER_SAMPLES),
    potentialValues: hasSamples ? { true: 1 } : { true: 0 },
    enabledValues: initialMerchandisings.includes(MerchandisingType.WebSampled)
      ? ['1']
      : [],
    displayNames: { true: TAG_SAMPLES },
  });

  filters.push({
    name: FILTER_FAVORITES,
    code: FILTER_FAVORITES,
    urlFriendlyName: toKebab(FILTER_FAVORITES),
    potentialValues: { true: 0 },
    enabledValues: [],
    displayNames: { true: FILTER_FAVORITES },
  });
};

const mapGroupFacetValuesToNames = (
  result: V2.ISearchItemsResult,
  itemGroups: IItemGroup[]
): void => {
  for (const level of resolveGroupLevels(itemGroups)) {
    const facet = result.facets?.find((f) => f.code === level.facetCode);
    if (!facet?.values) continue;
    facet.values = facet.values.map((value) => {
      const group = itemGroups.find(
        (ig) => ig.type === level.groupType && ig.id.indexOf(value.code) !== -1
      );
      const displayName = group?.name || value.code;
      return {
        ...value,
        code: slugify(displayName, { lower: true }),
        name: displayName,
      };
    });
  }
};

/**
 * Rewrites group filter codes (`brand`, `collection`, `line`) into the CiC
 * facet keys the search API expects (`brands`, `collections`, `productLines`),
 * resolving each filter value name to its item group id.
 */
const mapGroupFiltersToFacetKeys = (
  initialCharacteristics: ICharacteristicsFilter,
  itemGroups: IItemGroup[]
): ICharacteristicsFilter => {
  const newCharacteristics = { ...initialCharacteristics };

  for (const level of resolveGroupLevels(itemGroups)) {
    const names = initialCharacteristics[level.filterCode];
    if (names) {
      newCharacteristics[level.facetCode] = names
        .map(
          (name) =>
            itemGroups
              .filter((x) => x.type === level.groupType)
              .find((ig) => SlugEquals(ig.name, name))?.id
        )
        .filter((id): id is string => id !== undefined);
    }
    delete newCharacteristics[level.filterCode];
  }

  return newCharacteristics;
};

const filterByRestrictedTypes = (
  filters: Filter[],
  config: CatalogConfig,
  currentProductType?: string
): Filter[] => {
  if (!currentProductType) return filters;
  try {
    return filters.filter((filter) => {
      const filterConfig = config.filterConfig[filter.code ?? ''];
      if (!filterConfig?.restrictedTypes?.length) return true;
      return filterConfig.restrictedTypes.includes(currentProductType);
    });
  } catch (error) {
    logger.withError(error).error('Error filtering by restricted types:');
    return filters;
  }
};

// ============================================================
// CiCSearchService
// ============================================================

export class CiCSearchService {
  constructor(private readonly strategy: ItemCategoryStrategy) {}

  private async searchItems({
    catalogVersionId,
    characteristics,
    groupRefs,
    searchText = '',
    offset = 0,
    pageSize = DEFAULT_PAGE_SIZE,
  }: SearchItemsParams): Promise<V2.ISearchItemsResult | null> {
    try {
      const sanitizedCharacteristics = sanitizeCharacteristics(characteristics);

      const rawTypes = characteristics[CHAR_TYPE];
      const finalBaseItemTypes = rawTypes?.map((type) =>
        this.strategy.mapTypeForAPI(type)
      );

      const refCodes: string[] = [];
      const upids = characteristics[CHAR_UPIDS];
      refCodes.push(
        ...(upids
          ? upids.filter(Boolean).map((upid) => `manufCode.${upid}`)
          : [])
      );
      const skus = characteristics[CHAR_SKUS];
      refCodes.push(
        ...(skus ? skus.filter(Boolean).map((sku) => `sku.${sku}`) : [])
      );

      const response = await globalThis.CiCAPI.content.v2.searchItems(
        searchText,
        {
          catalogVersionIds: [catalogVersionId],
          classificationFilter: {
            characteristics: sanitizedCharacteristics,
            baseItemTypes: finalBaseItemTypes,
            catalogs: characteristics[CHAR_CATALOGS],
          },
          groupRefs: isEmpty(groupRefs) ? undefined : groupRefs,
          refCodes,
          useFilterConfig: false,
          nbPerPage: pageSize,
          visibleOnly: true,
          offset,
        }
      );
      return response.result ?? null;
    } catch (error) {
      logger.withError(error).error('Error searching items:');
      throw new Error('Failed to search items');
    }
  }

  private transformFacetToFilter(
    facet: IFacet,
    config: CatalogConfig,
    levels: GroupLevel[]
  ): Filter {
    if (facet.code === FACET_BASE_ITEM_TYPES) {
      facet.code = CHAR_TYPE;
      facet.values = facet.values?.map((value) => ({
        ...value,
        code: value.code
          ? this.strategy.cleanTypePrefix(value.code)
          : value.code,
      }));
      facet.values = facet.values?.map((value) => ({
        ...value,
        code: this.strategy.mapTypeForUrl(value.code ?? ''),
      }));
    }
    const level = levels.find((l) => l.facetCode === facet.code);
    if (level) facet.code = level.filterCode;

    const filterConfig = config.filterConfig[facet.code];
    const uom = filterConfig?.uom ?? '';
    let valueDisplayNames = filterConfig?.valueDisplayNames;
    if (uom && valueDisplayNames) {
      logger.warn(
        `Ignoring valueDisplayNames for facet "${facet.code}": value overrides are not supported on facets with a unit of measure ("${uom}").`
      );
      valueDisplayNames = undefined;
    }

    return {
      name: facet.name ?? '',
      code: facet.code,
      urlFriendlyName: toKebab(facet.code),
      potentialValues:
        facet.values?.reduce(
          (acc: Record<string, number>, value: IFacetValue) => {
            acc[value.code] = value.count;
            return acc;
          },
          {}
        ) ?? {},
      enabledValues: [],
      displayNames:
        facet.values?.reduce(
          (acc: Record<string, string>, value: IFacetValue) => {
            acc[value.code] = `${value.name ?? value.code} ${uom}`.trim();
            return acc;
          },
          {}
        ) ?? {},
      displayNameKeys: valueDisplayNames,
      ...(uom ? { uom } : {}),
    };
  }

  private parseFacetsAsFilter(
    result: V2.ISearchItemsResult | null,
    config: CatalogConfig,
    levels: GroupLevel[]
  ): Filter[] {
    if (!result?.facets) return [];
    try {
      const filteredFacets = result.facets.filter(
        (facet) => !config.excludedFilters?.includes(facet.code.toLowerCase())
      );
      const sortedFacets = sortWithPriority(
        filteredFacets,
        config.filterPriorities,
        (x) => x.code
      );
      const dedupedFacets = deduplicateFacets(sortedFacets);
      const facets = dedupedFacets.filter(
        (f) =>
          !GROUP_FACET_CODES[f.code] ||
          levels.some((l) => l.facetCode === f.code)
      );
      for (const facet of facets) {
        const level = levels.find((l) => l.facetCode === facet.code);
        const configKey = level?.configKey ?? facet.code;
        if (configKey in config.filterConfig) {
          facet.name = config.filterConfig[configKey].displayName;
        }
      }
      return facets.map((facet) =>
        this.transformFacetToFilter(facet, config, levels)
      );
    } catch (error) {
      logger.withError(error).error('Error parsing facets:');
      return [];
    }
  }

  private applyEnabledValuesToFilters(
    filters: Filter[],
    initialCharacteristics: ICharacteristicsFilter
  ): void {
    for (const filter of filters) {
      let characteristicValues = initialCharacteristics[filter.code ?? ''];
      if (filter.code === CHAR_TYPE && characteristicValues) {
        characteristicValues = characteristicValues.map((value) =>
          this.strategy.mapTypeForUrl(value)
        );
      }
      if (characteristicValues) {
        filter.enabledValues = characteristicValues;
      }
    }
  }

  private async createProductFromItem(
    item: V2.IItem,
    variants: V2.IItem[],
    groups: IItemGroup[]
  ): Promise<Partial<PaginatedProduct>> {
    const groupRefs = item.groupRefs ?? [];
    const itemGroups = groupRefs.map((gr) => groups.find((ig) => ig.id === gr));
    const lineGroupType = productLineGroupType(groups);
    const productNameGroup = itemGroups.find(
      (group) => group?.type === lineGroupType
    );
    const brandNameGroup = itemGroups.find(
      (group) => group?.type === ITEM_GROUP_BRAND
    );

    const { slug, sku } = decodeItemCode(item.code);
    const productGroupId = productNameGroup?.id ?? '';
    const isSpreadColor = isSpreadItem(item);
    const filteredVariants = isSpreadColor
      ? []
      : variants.filter(
          (variant) =>
            variant.groupRefs?.includes(productGroupId) &&
            !isSpreadItem(variant)
        );

    const swatches: Swatches[] = filteredVariants
      .map((variant) => {
        const { slug: variantSlug, sku: variantSku } = decodeItemCode(
          variant.code
        );
        const { tags, hasSample, hasVisualization } = cicMerchandisingTags(
          variant.context?.merchandising
        );
        return {
          colorName: variant.names?.main ?? '',
          imageURL: variant.images?.main?.uri ?? '',
          slug: variantSlug,
          sku: variantSku,
          UPID: variant.refCodes?.manufCode ?? '',
          hasVisualization,
          hasSample,
          tags,
          unitPrice: Number(variant.refCodes?.others?.pricing ?? 0),
          sequenceOrder: variant.sequenceOrder,
        };
      })
      .sort((a, b) => a.colorName.localeCompare(b.colorName));

    const { tags, hasSample, hasVisualization } = cicMerchandisingTags(
      item.context?.merchandising
    );
    const baseItemType = item.classification?.baseItemType ?? '';
    const itemStrategy = this.strategy.resolveForBaseItemType(baseItemType);
    const characteristics = item.classification?.characteristics as
      | Record<string, string | string[] | number | undefined>
      | undefined;

    return {
      id: item.id,
      productName: productNameGroup?.name ?? '',
      colorName: item.names?.main ?? '',
      imageURL: item.images?.main?.uri ?? '',
      productType: cicProductTypeName(baseItemType),
      UPID: item.refCodes?.manufCode,
      brandName: brandNameGroup?.name ?? '',
      tags,
      slug,
      sku: sku ?? 'missing-sku',
      swatches,
      isSpreadColor,
      colorCount: filteredVariants.length > 0 ? filteredVariants.length : 1,
      hasVisualization,
      hasSample,
      unitPrice: Number(item.refCodes?.others?.pricing ?? 0),
      priceDescription: itemStrategy.getPriceDescription(baseItemType),
      acRating: characteristics?.['abrasionCriteriaRating'] as
        | string
        | number
        | undefined,
      wearLayer: characteristics?.['wearLayerThickness'] as
        | string
        | number
        | undefined,
      material: characteristics?.['material'] as string | string[] | undefined,
      thickness: (item.dimensions as { height?: number } | undefined)?.height,
    };
  }

  async fetchVariants(
    catalogVersionId: string,
    variantGroupIds: string[]
  ): Promise<V2.IItem[]> {
    if (variantGroupIds.length === 0) return [];

    const payload = {
      catalogVersionId,
      characteristics: {},
      groupRefs: variantGroupIds,
      searchText: '',
      pageSize: VARIANTS_FETCH_PAGE_SIZE,
    };

    const firstResult = await this.searchItems({ ...payload, offset: 0 });
    if (!firstResult?.items?.length) return [];

    const items = [...firstResult.items];
    const total = firstResult.total ?? 0;
    if (items.length >= total) return items;

    const remainingOffsets: number[] = [];
    for (
      let offset = items.length;
      offset < total;
      offset += VARIANTS_FETCH_PAGE_SIZE
    ) {
      remainingOffsets.push(offset);
    }

    const remainingResults = await Promise.all(
      remainingOffsets.map((offset) => this.searchItems({ ...payload, offset }))
    );
    for (const result of remainingResults) {
      if (result?.items) items.push(...result.items);
    }
    return items;
  }

  async getFilters({
    catalogVersionId,
    characteristics: initialCharacteristics,
    groupRefs = [],
    searchText = '',
  }: GetFiltersParams): Promise<{ filters: Filter[]; total: number }> {
    const itemGroups = await fetchItemGroups(catalogVersionId);
    const finalCharacteristics = mapGroupFiltersToFacetKeys(
      initialCharacteristics,
      itemGroups
    );
    const groupRefsExceptSRPPriority = groupRefs.filter(
      (gr) => gr !== GROUP_REF_SRP_PRIORITY
    );

    const filterResult = await this.searchItems({
      catalogVersionId,
      characteristics: finalCharacteristics,
      groupRefs: groupRefsExceptSRPPriority,
      offset: 0,
      searchText,
      pageSize: MIN_FILTER_RESULT_SIZE,
    });

    if (!filterResult) return { filters: [], total: 0 };

    const config = await fetchCatalogConfig();
    mapGroupFacetValuesToNames(filterResult, itemGroups);

    const levels = resolveGroupLevels(itemGroups);
    const filters = this.parseFacetsAsFilter(filterResult, config, levels);
    this.applyEnabledValuesToFilters(filters, initialCharacteristics);
    mapMerchandisingsToTags(filters, initialCharacteristics);

    const currentProductType = initialCharacteristics[CHAR_TYPE]?.[0];
    const finalFilters = filterByRestrictedTypes(
      filters,
      config,
      currentProductType
    );
    return { filters: finalFilters, total: filterResult.total };
  }

  async search({
    catalogVersionId,
    initialPage,
    characteristics: initialCharacteristics,
    excludeFilters,
    excludeVariants,
    pageSize = DEFAULT_PAGE_SIZE,
    groupRefs = [],
    searchText = '',
  }: SearchParams): Promise<SearchResult> {
    const offset = ((initialPage ?? 1) - 1) * pageSize;
    const masterItemGroups = await fetchItemGroups(catalogVersionId);
    const finalCharacteristics = mapGroupFiltersToFacetKeys(
      initialCharacteristics,
      masterItemGroups
    );

    let colorCount = 0;
    const filters: Filter[] = [];
    if (!excludeFilters) {
      const filterData = await this.getFilters({
        catalogVersionId,
        characteristics: initialCharacteristics,
        groupRefs,
        searchText,
      });
      colorCount = filterData.total;
      filters.push(...filterData.filters);
    }

    const productResult = await this.searchItems({
      catalogVersionId,
      characteristics: finalCharacteristics,
      groupRefs,
      offset,
      searchText,
      pageSize,
    });

    if (!productResult || (!excludeFilters && filters.length === 0)) {
      return { products: [], filters: [], productCount: 0, colorCount: 0 };
    }

    const lineGroupType = productLineGroupType(masterItemGroups);
    const productLineGroups = productResult.items.map((item) => {
      const itemGroups = item.groupRefs?.map((gr) =>
        masterItemGroups.find((ig) => ig.id === gr)
      );
      return itemGroups?.find((group) => group?.type === lineGroupType);
    });
    const variantGroupIds = Array.from(
      new Set(
        productLineGroups
          .map((group) => group?.id)
          .filter((id): id is string => id !== undefined)
      )
    );

    let variantItems: V2.IItem[] = [];
    if (!excludeVariants) {
      const proposals = await Promise.allSettled(
        productResult.items.map((item) =>
          globalThis.CiCAPI.content.v2.getItemVariantProposals(
            { itemId: item.id },
            { variesBy: 'style' }
          )
        )
      );
      if (proposals.length === 0) {
        variantItems = await this.fetchVariants(
          catalogVersionId,
          variantGroupIds
        );
      } else {
        variantItems = proposals
          .filter(
            (
              result
            ): result is PromiseFulfilledResult<
              IAPIResponse<V2.IItemVariantProposal[]>
            > => result.status === 'fulfilled'
          )
          .flatMap((result) => result.value)
          .flatMap((proposal) => proposal.result ?? []);
      }
    }

    const productResults = await Promise.allSettled(
      productResult.items?.map((item) =>
        this.createProductFromItem(item, variantItems, masterItemGroups)
      ) ?? []
    );
    const products = productResults
      .filter(
        (result): result is PromiseFulfilledResult<Partial<PaginatedProduct>> =>
          result.status === 'fulfilled'
      )
      .map((result) => result.value);

    return {
      products,
      filters,
      colorCount,
      productCount: productResult.total,
    };
  }

  async getSwatchesForProduct(
    product: Partial<PaginatedProduct>
  ): Promise<Swatches[]> {
    const defaultSwatch: Swatches = {
      colorName: product.colorName ?? '',
      imageURL: product.imageURL ?? '',
      slug: product.slug ?? '',
      sku: product.sku ?? '',
      UPID: product.UPID ?? '',
      hasSample: product.hasSample ?? false,
      tags: product.tags ?? [],
      unitPrice: product.unitPrice ?? 0,
      hasVisualization: product.hasVisualization ?? false,
    };

    if (!product.id) return [defaultSwatch];

    const catalogVersionId = product.id.split('.')[0];

    // Fetch variant proposals (for groupRefs) and item groups (cached) in parallel
    const [response, itemGroups] = await Promise.all([
      globalThis.CiCAPI.content.v2
        .getItemVariantProposals({ itemId: product.id }, { variesBy: 'style' })
        .catch(() => ({ result: [] })),
      fetchItemGroups(catalogVersionId),
    ]);

    const proposalVariants: V2.IItem[] = (response as any).result ?? [];

    if (proposalVariants.length === 0) return [defaultSwatch];

    // getItemVariantProposals strips context.merchandising from the returned items,
    // so hasVisualization/hasSample would always be false if we used those directly.
    // Instead, find the product-line groupRef and call fetchVariants (via searchItems)
    // which returns full IItem objects with context.merchandising populated.
    const lineGroupType = productLineGroupType(itemGroups);
    const productLineGroupRef = proposalVariants[0]?.groupRefs?.find((ref) =>
      itemGroups.some((g) => g.id === ref && g.type === lineGroupType)
    );

    const fullVariants: V2.IItem[] = productLineGroupRef
      ? await this.fetchVariants(catalogVersionId, [productLineGroupRef]).catch(
          () => proposalVariants
        )
      : proposalVariants;

    const variantSwatches: Swatches[] = fullVariants
      .filter((variant) => variant.id !== product.id && !isSpreadItem(variant))
      .map((variant) => {
        const { slug: variantSlug, sku: variantSku } = decodeItemCode(
          variant.code
        );
        const { tags, hasSample, hasVisualization } = cicMerchandisingTags(
          variant.context?.merchandising
        );
        return {
          colorName: variant.names?.main ?? '',
          imageURL: variant.images?.main?.uri ?? '',
          slug: variantSlug,
          sku: variantSku,
          UPID: variant.refCodes?.manufCode ?? '',
          hasVisualization,
          hasSample,
          tags,
          unitPrice: Number(variant.refCodes?.others?.pricing ?? 0),
          sequenceOrder: variant.sequenceOrder,
        };
      });

    return [defaultSwatch, ...variantSwatches].sort((a, b) =>
      a.colorName.localeCompare(b.colorName)
    );
  }
}

// ============================================================
// Module-level exports (backward compat)
// ============================================================

export const fetchVariants = (
  catalogVersionId: string,
  variantGroupIds: string[]
): Promise<V2.IItem[]> => {
  const service = new CiCSearchService(
    getItemCategoryStrategy(CatalogCategory.Flooring)
  );
  return service.fetchVariants(catalogVersionId, variantGroupIds);
};

// ============================================================
// Hook
// ============================================================

export const useCiCSearch = (
  categories: CatalogCategory | CatalogCategory[] = [
    CatalogCategory.Flooring,
    CatalogCategory.Worktop,
    CatalogCategory.Cabinetry,
    CatalogCategory.Bath,
  ]
): UseCiCSearchResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const categoryKey = Array.isArray(categories)
    ? [...categories].sort().join(',')
    : categories;

  const serviceRef = useRef<CiCSearchService | null>(null);
  const keyRef = useRef('');
  if (keyRef.current !== categoryKey) {
    keyRef.current = categoryKey;
    serviceRef.current = new CiCSearchService(createStrategy(categories));
  }
  const service = serviceRef.current!;

  const getFilters = useCallback(
    (params: GetFiltersParams) => service.getFilters(params),
    [service]
  );

  const search = useCallback(
    async (params: SearchParams): Promise<SearchResult> => {
      setIsLoading(true);
      setError(null);
      try {
        return await service.search(params);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'An unknown error occurred';
        setError(errorMessage);
        logger.withError(err).error('Search error:');
        return { products: [], filters: [], productCount: 0, colorCount: 0 };
      } finally {
        setIsLoading(false);
      }
    },
    [service]
  );

  const getSwatchesForProduct = useCallback(
    (product: Partial<PaginatedProduct>) =>
      service.getSwatchesForProduct(product),
    [service]
  );

  return { search, getFilters, getSwatchesForProduct, isLoading, error };
};
